import React, { forwardRef } from 'react';
// @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// Type for common props that all Three.js elements can have
interface BaseProps {
  children?: React.ReactNode;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
}

// Props for group
interface GroupProps extends BaseProps {
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>) => void;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
}

// Props for mesh
interface MeshProps extends BaseProps {
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>) => void;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
}

// Props for geometries
interface GeometryProps {
  args?: any[];
  attach?: string;
}

// Props for materials
interface MaterialProps {
  color?: string | number;
  transparent?: boolean;
  opacity?: number;
  wireframe?: boolean;
  side?: number;
  emissive?: string | number;
  emissiveIntensity?: number;
}

// Props for lights
interface LightProps extends BaseProps {
  intensity?: number;
  color?: string | number;
  castShadow?: boolean;
}

// Wrapper components for Three.js elements
// These components simply pass through their props to the actual Three.js JSX elements

// Using forwardRef for components that need refs
export const Group = forwardRef<THREE.Group, GroupProps>((props, ref) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <group {...props} ref={ref} />;
});

export const Mesh = forwardRef<THREE.Mesh, MeshProps>((props, ref) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <mesh {...props} ref={ref} />;
});

export const PlaneGeometry: React.FC<GeometryProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <planeGeometry {...props} />;
};

export const SphereGeometry: React.FC<GeometryProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <sphereGeometry {...props} />;
};

export const CircleGeometry: React.FC<GeometryProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <circleGeometry {...props} />;
};

export const RingGeometry: React.FC<GeometryProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <ringGeometry {...props} />;
};

export const BufferGeometry: React.FC<GeometryProps & { children?: React.ReactNode }> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <bufferGeometry {...props} />;
};

export const BufferAttribute: React.FC<{
  attach: string;
  array: Float32Array;
  count: number;
  itemSize: number;
}> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <bufferAttribute {...props} />;
};

export const MeshBasicMaterial: React.FC<MaterialProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <meshBasicMaterial {...props} />;
};

export const MeshStandardMaterial: React.FC<MaterialProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <meshStandardMaterial {...props} />;
};

export const MeshPhongMaterial: React.FC<MaterialProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <meshPhongMaterial {...props} />;
};

export const LineBasicMaterial: React.FC<MaterialProps & { linewidth?: number }> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <lineBasicMaterial {...props} />;
};

export const PointsMaterial: React.FC<MaterialProps & { 
  size?: number;
  vertexColors?: boolean;
  sizeAttenuation?: boolean;
}> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <pointsMaterial {...props} />;
};

export const Line: React.FC<BaseProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <line {...props} />;
};

// Use forwardRef for Points to properly handle the ref
export const Points = forwardRef<THREE.Points, BaseProps>((props, ref) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <points {...props} ref={ref} />;
});

export const AmbientLight: React.FC<LightProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <ambientLight {...props} />;
};

export const PointLight: React.FC<LightProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <pointLight {...props} />;
};

export const DirectionalLight: React.FC<LightProps> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <directionalLight {...props} />;
};

export const SpotLight: React.FC<LightProps & {
  angle?: number;
  penumbra?: number;
  distance?: number;
}> = (props) => {
  // @ts-ignore - Ignore TypeScript errors for Three.js JSX elements
  return <spotLight {...props} />;
};